import {useRef} from "react";
import {toast} from "react-toastify";
import styled from "styled-components";
import {engineApi} from "../../api/engine-api";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
    addControlDeliveriesAction,
    clearControlDeliveriesAction,
    deliveryState,
} from "../../app/reducers/delivery-slice";
import {setPreviewAction} from "../../app/reducers/schedule-slice";
import theme from "../../style/theme";
import {ControlDelivery} from "../../types/delivery/control-delivery";
import {DeliveryGroupType} from "../../types/schedule/delivery-group-type";
import {ContainedBlueButton} from "../button/ContainedBlueButton";
import CloseLineIcon from "../icons/CloseLineIcon";
import InformationIcon from "../icons/InformationIcon";
import {CostDisplayComponent} from "../label/cost-display-component";
import {displayCost} from "../../utils/routing-cost/routing-cost-calculator";
import {useClickOutside} from "../../utils/click/handle-click";

interface Props {
    closeDialog: () => void;
    taskNums: number;
}

export const RouteOptimizationDialog: React.FC<Props> = (
    {
        closeDialog,
        taskNums,
    }
) => {
    const ref = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const {controlDeliveries, displayDeliveries} =
        useAppSelector(deliveryState);

    useClickOutside(ref, () => closeDialog());

    const getPreview = async () => {
        try {
            const payload = controlDeliveries.map((controlDelivery) => {
                return {
                    id: controlDelivery.delivery.id,
                    type: controlDelivery.type,
                };
            });

            const response = await engineApi.pdptw({deliverySegments: payload});
            const recommandDeliveries = response.data.deliverySegments
                .map((segment) => {
                    const recommandDelivery = findRecommandDelivery(segment.id);
                    if (recommandDelivery) {
                        return {
                            key: recommandDelivery.id + DeliveryGroupType.PickUp,
                            delivery: recommandDelivery,
                            type: DeliveryGroupType.PickUp,
                        };
                    }
                })
                .filter((delivery) => delivery) as ControlDelivery[];

            toast.success("동선 최적화가 반영되었습니다. 스케줄을 확인해주세요.");

            dispatch(clearControlDeliveriesAction());
            dispatch(
                addControlDeliveriesAction({
                    deliveries: recommandDeliveries,
                })
            );
            dispatch(setPreviewAction({previewDto: response.data}));

            closeDialog();
        } catch (error) {
            console.log(error);
        }
    };

    const findRecommandDelivery = (recommandDeliveryId: string) => {
        const deliveriesInDisplayDeliveries = displayDeliveries.flatMap(
            (displayDelivery) => {
                return displayDelivery.deliveries.map((delivery) => delivery);
            }
        );
        return deliveriesInDisplayDeliveries.find((delivery) => {
            return delivery.id === recommandDeliveryId;
        });
    };

    return (
        <Container ref={ref}>
            <CloseIconWrapper onClick={closeDialog}>
                <CloseLineIcon
                    style={{width: "24px", height: "24px"}}
                    fill={theme.palette.primitives.gray300}
                />
            </CloseIconWrapper>
            <InformationIcon fill="#075ee1"/>
            <Title>유의해주세요</Title>
            <Body>
                <BodyText>
                    1. 동선 최적화 시에 아래 직접 등록하신 첫 번째 상차지는 그대로
                    고정됩니다.
                </BodyText>
                <BodyText>
                    2. 상차는 지정된 시간보다 최대 30분 빠르게 상차가 완료되도록 동선이
                    만들어질 수 있으며, 전달은 상차 이후 지정된 시간까지 언제든 배송될 수
                    있는 설정으로 최적화됩니다.
                </BodyText>
                <BodyText>
                    3. 지정된 시간까지 배송이 불가하다고 판단될 경우 동선 최적화가
                    불가능할 수 있습니다.
                </BodyText>
                {/*<BodyText>*/}
                {/*  4. 동선 최적화 1회당 비용이 발생합니다. 중복된 동선을 반복적으로*/}
                {/*  최적화 시도할 경우 차단될 수 있습니다.*/}
                {/*</BodyText>*/}
            </Body>
            <CostDisplayComponent
                fontSize={14}
                backgroundColor={"#DBF1FF"}
                textColor={"#14439C"}
            >
                {displayCost(1, taskNums)}
            </CostDisplayComponent>
            <ButtonContainer>
                <ContainedBlueButton
                    width={"113px"}
                    height={"33px"}
                    text={"시작하기"}
                    onClick={getPreview}
                />
            </ButtonContainer>
        </Container>
    );
};

const Container = styled.div`
  position: absolute;
  background: #ffffff;
  width: calc(100% - 48px);
  height: max-content;
  top: 60px;
  z-index: 99;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  gap: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CloseIconWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  cursor: pointer;
`;

const Title = styled.div`
  color: #075ee1;
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
`;

const Body = styled.div`
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: max-content;
`;

const BodyText = styled.div`
  text-align: center;
  color: #4d535c;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
`;

const ButtonContainer = styled.div`
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
